import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Image,
  Input,
  Modal,
  Segmented,
  Select,
  Spin,
  Switch,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CarOutlined,
  LinkOutlined,
  SendOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  addTicketComment,
  getAssignableUsers,
  getSupportTicket,
  TICKET_CATEGORIES,
  TICKET_STATUS_COLORS,
  TICKET_STATUSES,
  updateSupportTicket,
} from '../modules/support-tickets/api';
import PartnerLogo from '../components/PartnerLogo';
import TicketLinkFields, { boxLabel } from '../components/TicketLinkFields';
import { getPartners } from '../modules/partners/api';

const fmtTime = (iso) => (iso ? dayjs(iso).format('DD MMM YYYY HH:mm') : '—');

const ROLE_ICONS = {
  DRIVER: <CarOutlined />,
  SYSTEM: <ToolOutlined />,
  PARTNER_VIEWER: <UserOutlined />,
};

// Linked-order statuses where the operator has a recovery move to offer.
const FAILED_ORDER_STATUSES = ['skipped', 'delivery_failed', 'pickup_failed'];

/**
 * OVO-117 — ticket detail: status controls, linked-entity chips, the
 * conversation thread (internal notes greyed + tagged), and quick actions
 * when the linked order is in a failed state (Recover / Reschedule live on
 * the order page's OperatorActionPanel — the button lands the operator there).
 */
const TicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['routes', 'common']);
  const host = useSelector((s) => s.auth?.user?.host);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [commentBody, setCommentBody] = useState('');
  const [commentInternal, setCommentInternal] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  // #132 — link / re-link the ticket to a box, site or recurring schedule.
  const [linksOpen, setLinksOpen] = useState(false);
  const [linkInitial, setLinkInitial] = useState({});
  const [linkForm] = Form.useForm();
  const [partnerOptions, setPartnerOptions] = useState([]);
  const linkPartnerId = Form.useWatch('partnerId', linkForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { response } = await getSupportTicket(id);
      if (response?.id) setTicket(response);
      else messageApi.error(response?.message || t('common:error'));
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    load();
    getAssignableUsers().then(({ response }) => {
      if (Array.isArray(response)) {
        setAssignees(response.map((u) => ({ value: u.id, label: u.name || u.email })));
      }
    });
  }, [load]);

  const patch = async (data, successMsg) => {
    setSaving(true);
    try {
      const { response } = await updateSupportTicket(id, data);
      if (response?.id) {
        setTicket(response);
        if (successMsg) messageApi.success(successMsg);
      } else {
        messageApi.error(response?.message || t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setSaving(false);
    }
  };

  const openLinks = () => {
    setLinkInitial({
      partnerId: ticket.partnerId ?? undefined,
      collectionPointId: ticket.collectionPointId ?? undefined,
      siteId: ticket.collectionPointId ? undefined : ticket.siteId ?? undefined,
      orderScheduleId: ticket.orderScheduleId ?? undefined,
    });
    if (!partnerOptions.length) {
      getPartners({ take: 300 }).then(({ response }) => {
        setPartnerOptions((response?.partners || []).map((p) => ({ value: p.id, label: p.name })));
      });
    }
    setLinksOpen(true);
  };

  const saveLinks = async () => {
    const v = linkForm.getFieldsValue();
    // null clears a link; a box re-derives its own site server-side.
    await patch(
      {
        partnerId: v.partnerId ?? null,
        collectionPointId: v.collectionPointId ?? null,
        siteId: v.siteId ?? null,
        orderScheduleId: v.orderScheduleId ?? null,
      },
      t('tickets.linksSaved'),
    );
    setLinksOpen(false);
  };

  const submitComment = async () => {
    if (!commentBody.trim()) return;
    setSaving(true);
    try {
      const { response } = await addTicketComment(id, commentBody.trim(), commentInternal);
      if (response?.id) {
        setCommentBody('');
        setCommentInternal(false);
        load();
      } else {
        messageApi.error(response?.message || t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !ticket) {
    return (
      <div className="Content" style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (!ticket) return null;

  const orderFailed = ticket.order && FAILED_ORDER_STATUSES.includes(String(ticket.order.status).toLowerCase());

  const chip = (label, to) => (
    <Link to={to}>
      <Tag color="blue" style={{ cursor: 'pointer' }}>
        {label}
      </Tag>
    </Link>
  );

  return (
    <div className="TicketDetailPage Content" style={{ paddingBottom: 24 }}>
      {contextHolder}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 280, flex: 1 }}>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/tickets')}>
              {t('tickets.backToList')}
            </Button>
            <h2 style={{ margin: '8px 0 4px' }}>
              #{ticket.id} — {ticket.subject}{' '}
              <Tag color={TICKET_STATUS_COLORS[ticket.status]}>
                {t(`tickets.statuses.${ticket.status}`, ticket.status)}
              </Tag>
            </h2>
            <div style={{ color: 'var(--text-muted)' }}>
              {t('tickets.openedBy', {
                role: ticket.creatorRole,
                name: ticket.createdBy?.name || ticket.createdBy?.email || '—',
                date: fmtTime(ticket.createdAt),
              })}
              {ticket.resolvedAt &&
                ` · ${t('tickets.resolvedBy', {
                  name: ticket.resolvedBy?.name || '—',
                  date: fmtTime(ticket.resolvedAt),
                })}`}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <Segmented
              value={ticket.status}
              disabled={saving}
              onChange={(s) => patch({ status: s }, t('tickets.statusChanged'))}
              options={TICKET_STATUSES.map((s) => ({
                value: s,
                label: t(`tickets.statuses.${s}`, s),
              }))}
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>
                {t('tickets.internal')}{' '}
                <Tooltip title={t('tickets.internalHint')}>
                  <Switch
                    size="small"
                    checked={ticket.internal}
                    disabled={saving}
                    onChange={(v) => patch({ internal: v })}
                  />
                </Tooltip>
              </span>
              <Select
                size="small"
                style={{ minWidth: 170 }}
                allowClear
                placeholder={t('tickets.assignTo')}
                value={ticket.assigneeId ?? undefined}
                options={assignees}
                onChange={(v) => patch({ assigneeId: v ?? null })}
              />
              <Select
                size="small"
                style={{ minWidth: 170 }}
                value={ticket.category}
                options={TICKET_CATEGORIES.map((c) => ({
                  value: c,
                  label: t(`tickets.categories.${c}`, c),
                }))}
                onChange={(v) => patch({ category: v })}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {ticket.partner && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <PartnerLogo name={ticket.partner.name} logoKey={ticket.partner.logoFile?.key} size={18} />
              {chip(ticket.partner.name, `/admin/partners/${ticket.partner.id}`)}
            </span>
          )}
          {(ticket.order || ticket.orderCode) &&
            (ticket.order ? (
              chip(`${t('tickets.order')} ${ticket.order.orderCode}`, `/admin/orders/${ticket.order.orderCode}`)
            ) : (
              <Tooltip title={t('tickets.orderDeleted')}>
                <Tag>{`${t('tickets.order')} ${ticket.orderCode}`}</Tag>
              </Tooltip>
            ))}
          {ticket.route && chip(`${t('tickets.route')} #${ticket.route.id}`, `/admin/routes/${ticket.route.id}`)}
          {ticket.driver && chip(ticket.driver.name, `/admin/drivers/${ticket.driver.id}`)}
          {ticket.collectionPoint &&
            chip(
              t('tickets.box', { name: boxLabel(ticket.collectionPoint) }),
              `/admin/collection-points/${ticket.collectionPoint.id}`,
            )}
          {ticket.site &&
            chip(
              t('tickets.site', { name: ticket.site.name }),
              ticket.partnerId
                ? `/admin/partners/${ticket.partnerId}/sites?focusSiteId=${ticket.site.id}`
                : '/admin/sites',
            )}
          {ticket.orderSchedule &&
            chip(
              t('tickets.schedule', { id: ticket.orderSchedule.id }),
              ticket.partnerId ? `/admin/partners/${ticket.partnerId}/schedules` : '/admin/recurring',
            )}
          <Button size="small" type="link" icon={<LinkOutlined />} onClick={openLinks} data-testid="ticket-edit-links">
            {t('tickets.editLinks')}
          </Button>
        </div>
        <Modal
          title={t('tickets.linksTitle')}
          open={linksOpen}
          onCancel={() => setLinksOpen(false)}
          onOk={saveLinks}
          okText={t('common:save')}
          confirmLoading={saving}
          destroyOnClose
        >
          <Form form={linkForm} layout="vertical" preserve={false} initialValues={linkInitial}>
            <Form.Item name="partnerId" label={t('tickets.partner')}>
              <Select allowClear showSearch optionFilterProp="label" options={partnerOptions} />
            </Form.Item>
            <TicketLinkFields form={linkForm} partnerId={linkPartnerId} current={ticket} />
          </Form>
        </Modal>

        {orderFailed && (
          <Alert
            style={{ marginTop: 16 }}
            type="warning"
            showIcon
            message={t('tickets.orderFailedHint', { status: ticket.order.status })}
            action={
              <Button size="small" onClick={() => navigate(`/admin/orders/${ticket.order.orderCode}`)}>
                {t('tickets.openOrderActions')}
              </Button>
            }
          />
        )}

        {ticket.description && (
          <Card size="small" style={{ marginTop: 16, background: 'var(--surface-2)' }}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</div>
          </Card>
        )}

        {ticket.files?.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Image.PreviewGroup>
              {ticket.files.map((f) =>
                f.file?.key && host ? (
                  <Image key={f.id} width={100} src={`${host}/${f.file.key}`} style={{ marginRight: 8 }} />
                ) : null,
              )}
            </Image.PreviewGroup>
          </div>
        )}

        <h3 style={{ marginTop: 24 }}>{t('tickets.conversation')}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(ticket.comments || []).length === 0 && (
            <div style={{ color: 'var(--text-muted)' }}>{t('tickets.noComments')}</div>
          )}
          {(ticket.comments || []).map((c) => (
            <div
              key={c.id}
              style={{
                border: '1px solid #eee',
                borderRadius: 8,
                padding: '8px 12px',
                background: c.internal ? '#f5f0fa' : '#fff',
              }}
            >
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                {ROLE_ICONS[c.authorRole] || null} <b>{c.author?.name || c.author?.email || c.authorRole}</b>
                {' · '}
                {fmtTime(c.createdAt)}
                {c.internal && (
                  <Tag color="purple" style={{ marginLeft: 8 }}>
                    {t('tickets.internalTag')}
                  </Tag>
                )}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          <Input.TextArea
            rows={3}
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            placeholder={t('tickets.replyPlaceholder')}
            maxLength={5000}
          />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Checkbox checked={commentInternal} onChange={(e) => setCommentInternal(e.target.checked)}>
              {t('tickets.internalNote')}
            </Checkbox>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={saving}
              disabled={!commentBody.trim()}
              onClick={submitComment}
            >
              {t('tickets.send')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TicketDetailPage;
